import type { Resources } from '@/models/types';
import type { EventManager } from '@/systems/EventManager';
import type { TalentManager } from '@/systems/TalentManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { BuildingManager } from '@/systems/BuildingManager';
import { IdleCalculator } from '@/systems/IdleCalculator';

export class GameLoop {
  private readonly resources: ResourceManager;
  private readonly buildings: BuildingManager;
  private prestigeMultiplier: number;
  private eventManager: EventManager | null = null;
  private talentManager: TalentManager | null = null;

  constructor(resources: ResourceManager, buildings: BuildingManager, prestigeMultiplier: number) {
    this.resources = resources;
    this.buildings = buildings;
    this.prestigeMultiplier = prestigeMultiplier;
  }

  setEventManager(em: EventManager): void {
    this.eventManager = em;
  }

  setTalentManager(tm: TalentManager): void {
    this.talentManager = tm;
  }

  tick(deltaSeconds: number): void {
    const rates = this.buildings.getProductionRates();
    const production = IdleCalculator.calculateProduction(rates, deltaSeconds, this.prestigeMultiplier);

    // Talent: production boost
    const prodBonus = this.talentManager?.getTalentEffect('production') ?? 0;
    if (prodBonus > 0) {
      for (const rt of ['gold', 'food', 'faith', 'knowledge'] as const) {
        production[rt] *= (1 + prodBonus);
      }
    }

    // Event multipliers (per resource)
    if (this.eventManager) {
      for (const rt of ['gold', 'food', 'faith', 'knowledge'] as const) {
        production[rt] *= this.eventManager.getProductionMultiplier(rt);
      }
    }
    this.resources.add(production);
  }

  applyOfflineEarnings(elapsedSeconds: number): void {
    const rates = this.buildings.getProductionRates();
    const earnings = IdleCalculator.calculateOfflineEarnings(rates, elapsedSeconds, this.prestigeMultiplier);

    // Talent: offline bonus
    const offlineBonus = this.talentManager?.getTalentEffect('offline') ?? 0;
    if (offlineBonus > 0) {
      for (const rt of ['gold', 'food', 'faith', 'knowledge'] as const) {
        earnings[rt] *= (1 + offlineBonus);
      }
    }
    this.resources.add(earnings);
  }

  getState() {
    return {
      resources: this.resources.getAll(),
      buildings: this.buildings.getLevels(),
      prestigeMultiplier: this.prestigeMultiplier,
      productionRates: this.buildings.getProductionRates(),
      totalProductionRate: this.buildings.getTotalProductionRate(),
    };
  }

  setPrestigeMultiplier(multiplier: number): void {
    this.prestigeMultiplier = multiplier;
  }
}
